/**
 * Custom logger utility that conditionally logs based on environment
 * Only outputs logs in non-production environments by default
 */

import { createContext, useContext } from 'react';

// Determine if we're in development mode
const isDevelopment = process.env.NODE_ENV !== 'production';

// Create a logger object with methods that conditionally log
export const logger = {
  log: (...args) => {
    if (isDevelopment) {
      console.log(...args);
    }
  },
  warn: (...args) => {
    if (isDevelopment) {
      console.warn(...args);
    }
  },
  error: (...args) => {
    // We keep error logs even in production as they're important
    // In a real-world scenario, you might want to send these to an error tracking service
    console.error(...args);
  },
  info: (...args) => {
    if (isDevelopment) {
      console.info(...args);
    }
  },
  debug: (...args) => {
    if (isDevelopment) {
      console.debug(...args);
    }
  },
  // Force logging even in production if needed
  always: {
    log: (...args) => console.log(...args),
    warn: (...args) => console.warn(...args),
    error: (...args) => console.error(...args),
    info: (...args) => console.info(...args),
    debug: (...args) => console.debug(...args),
  }
};

// Export loggerStatic for backward compatibility with existing code
export const loggerStatic = logger;

// Create a React context for logging
const LoggerContext = createContext({
  enabled: isDevelopment,
  ...logger
});

// Provider component for the logger
export const LoggerProvider = ({ children, enabled = isDevelopment }) => {
  const contextValue = {
    enabled,
    ...logger
  };
  
  return (
    <LoggerContext.Provider value={contextValue}>
      {children}
    </LoggerContext.Provider>
  );
};

// Hook to use the logger in components
export const useLogger = () => {
  const context = useContext(LoggerContext);
  if (context === undefined) {
    return logger; // Fall back to default logger if used outside provider
  }
  return context;
};

export default logger;
